// =====================================================
// RouterDB.jsx — Router & Hardware Katalog
// Erweitert mit vollständigen Specs, Infoblatt & Print
// =====================================================
import React, { useState, useEffect, useMemo, useCallback } from 'react';

function fmtEuro(v) { return (v || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }); }

function GlassCard({ children, glow = 'cyan', style = {}, onClick }) {
  const glowMap = {
    cyan:   { border: 'rgba(56,189,248,0.18)', bg: 'rgba(10,25,50,0.55)', shadow: 'rgba(56,189,248,0.08)' },
    green:  { border: 'rgba(0,245,212,0.18)',   bg: 'rgba(10,25,50,0.55)',  shadow: 'rgba(0,245,212,0.08)' },
    orange: { border: 'rgba(251,146,60,0.18)',  bg: 'rgba(10,25,50,0.55)', shadow: 'rgba(251,146,60,0.08)' },
    purple: { border: 'rgba(167,139,250,0.18)', bg: 'rgba(10,25,50,0.55)', shadow: 'rgba(167,139,250,0.08)' },
    pink:   { border: 'rgba(255,102,162,0.18)', bg: 'rgba(10,25,50,0.55)', shadow: 'rgba(255,102,162,0.08)' },
  };
  const c = glowMap[glow] || glowMap.cyan;
  return (
    <div onClick={onClick} style={{ borderRadius: 20, border: `1px solid ${c.border}`, background: c.bg, backdropFilter: 'blur(24px) saturate(180%)', WebkitBackdropFilter: 'blur(24px) saturate(180%)', boxShadow: `0 8px 32px ${c.shadow}, inset 0 1px 0 rgba(255,255,255,0.06)`, padding: '18px 16px', ...style }}>
      {children}
    </div>
  );
}

function SpecRow({ label, value, accent }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.02)', marginBottom: 3 }}>
      <span style={{ fontSize: '0.6rem', color: 'rgba(200,220,255,0.6)', fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: '0.62rem', color: accent || '#fff', fontWeight: 700, textAlign: 'right', maxWidth: '60%' }}>{value}</span>
    </div>
  );
}

function SectionHeader({ icon, title, color }) {
  return (
    <div style={{ fontSize: '0.68rem', fontWeight: 800, color: color || '#fb923c', marginBottom: 8, marginTop: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
      <span>{icon}</span> {title}
    </div>
  );
}

const printCSS = `
@media print {
  body > *:not(.print-infoblatt-overlay) { display: none !important; }
  .print-infoblatt-overlay { position: static !important; background: #fff !important; }
  .print-infoblatt-overlay * { color: #000 !important; background: transparent !important; border-color: #ccc !important; }
  .print-infoblatt-content { max-width: 100% !important; width: 100% !important; max-height: none !important; overflow: visible !important; box-shadow: none !important; border: none !important; padding: 20px !important; }
  .no-print { display: none !important; }
  .print-infoblatt-content .spec-grid { break-inside: avoid; }
}
`;

const TYPE_LABELS = { dsl: '🔌 DSL Router', cable: '📡 Kabel Router', '5g': '📶 5G HomeSpot', '5g-router': '📶 5G HomeSpot', repeater: '📡 Repeater', tv: '📺 TV & Streaming', iptv: '📺 TV & Streaming' };
const TYPE_COLORS = { dsl: 'cyan', cable: 'green', '5g': 'orange', '5g-router': 'orange', repeater: 'purple', tv: 'purple', iptv: 'pink' };
const TYPE_ICONS = { dsl: '🔌', cable: '📡', '5g': '📶', '5g-router': '📶', repeater: '📡', tv: '📺', iptv: '📺' };

export function RouterDB() {
  const [routers, setRouters] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedRouter, setSelectedRouter] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [detailTab, setDetailTab] = useState('details');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!document.getElementById('router-print-css')) {
      const style = document.createElement('style');
      style.id = 'router-print-css';
      style.textContent = printCSS;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    fetch('/api/routers')
      .then(r => r.json())
      .then(data => { setRouters(data.filter(d => d.active !== false)); setIsLoading(false); })
      .catch(() => { setRouters([]); setIsLoading(false); });
  }, []);

  const types = useMemo(() => {
    const t = [...new Set(routers.map(r => r.type))];
    return ['all', ...t];
  }, [routers]);

  const filtered = useMemo(() => {
    let result = routers;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(r => r.name.toLowerCase().includes(q) || r.brand.toLowerCase().includes(q));
    }
    if (selectedType !== 'all') result = result.filter(r => r.type === selectedType);
    return result;
  }, [routers, search, selectedType]);

  const handlePrint = useCallback(() => { window.print(); }, []);

  const openRouter = useCallback((router) => {
    setSelectedRouter(router);
    setDetailTab('details');
  }, []);

  /* =============== DETAIL MODAL =============== */
  const renderDetailModal = () => {
    if (!selectedRouter) return null;
    const r = selectedRouter;
    const ports = r.ports || {};
    const dd = r.displayDetails || {};
    const tel = r.telephony || {};
    const mesh = r.mesh || {};
    const sh = r.smartHome || {};
    const dim = r.dimensions || {};
    const pw = r.powerConsumption || {};
    const mf = r.mobilfunk || {};
    const stream = r.streaming || {};
    const audio = r.audio || {};

    return (
      <div className={detailTab === 'infoblatt' ? 'print-infoblatt-overlay' : ''} style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' }} onClick={() => setSelectedRouter(null)}>
        <div className={detailTab === 'infoblatt' ? 'print-infoblatt-content' : ''} onClick={e => e.stopPropagation()} style={{ background: 'rgba(10,20,50,0.96)', border: '1px solid rgba(251,146,60,0.2)', borderRadius: 24, padding: '24px 20px', maxWidth: 560, width: '95%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 80px rgba(0,0,0,0.6)' }}>

          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: '1.12rem', fontWeight: 900, color: '#fff' }}>{r.name}</div>
              <div style={{ fontSize: '0.62rem', color: 'rgba(200,220,255,0.65)', marginTop: 2 }}>{r.brand} · {TYPE_LABELS[r.type] || r.type}</div>
            </div>
            <button className="no-print" onClick={() => setSelectedRouter(null)} style={{ background: 'none', border: 'none', color: '#ff006e', fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
          </div>

          {/* Tab Toggle */}
          <div className="no-print" style={{ display: 'flex', gap: 4, marginBottom: 16, background: 'rgba(255,255,255,0.03)', borderRadius: 12, padding: 3 }}>
            <button onClick={() => setDetailTab('details')}
              style={{ flex: 1, padding: '8px 12px', borderRadius: 10, border: 'none', background: detailTab === 'details' ? 'rgba(251,146,60,0.15)' : 'transparent', color: detailTab === 'details' ? '#fb923c' : 'rgba(200,220,255,0.5)', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              📋 Details
            </button>
            <button onClick={() => setDetailTab('infoblatt')}
              style={{ flex: 1, padding: '8px 12px', borderRadius: 10, border: 'none', background: detailTab === 'infoblatt' ? 'rgba(0,245,212,0.15)' : 'transparent', color: detailTab === 'infoblatt' ? '#00f5d4' : 'rgba(200,220,255,0.5)', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              🖨️ Infoblatt
            </button>
          </div>

          {/* ===== DETAILS TAB ===== */}
          {detailTab === 'details' && (
            <>
              {/* Quick Specs */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 12 }}>
                <div style={{ padding: '10px', borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.6)', fontWeight: 700, textTransform: 'uppercase' }}>Kaufpreis</div>
                  <div style={{ fontSize: '0.82rem', fontWeight: 800, color: '#00f5d4', marginTop: 2 }}>{fmtEuro(r.price)}</div>
                </div>
                {r.purchasePrice > 0 && (
                  <div style={{ padding: '10px', borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.6)', fontWeight: 700, textTransform: 'uppercase' }}>Einkaufspreis</div>
                    <div style={{ fontSize: '0.82rem', fontWeight: 800, color: '#fb923c', marginTop: 2 }}>{fmtEuro(r.purchasePrice)}</div>
                  </div>
                )}
                {r.maxSpeed && (
                  <div style={{ padding: '10px', borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.6)', fontWeight: 700, textTransform: 'uppercase' }}>Max. Speed</div>
                    <div style={{ fontSize: '0.82rem', fontWeight: 800, color: '#fff', marginTop: 2 }}>{r.maxSpeed}</div>
                  </div>
                )}
                {r.wifi && (
                  <div style={{ padding: '10px', borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.6)', fontWeight: 700, textTransform: 'uppercase' }}>WLAN</div>
                    <div style={{ fontSize: '0.82rem', fontWeight: 800, color: '#fff', marginTop: 2 }}>{r.wifi}</div>
                  </div>
                )}
              </div>

              {/* WLAN Details */}
              {(dd.wifiStandard || dd.wifiBands || dd.wifiSpeed) && (
                <>
                  <SectionHeader icon="📶" title="WLAN Details" color="#38bdf8" />
                  <SpecRow label="Standard" value={dd.wifiStandard} />
                  <SpecRow label="Frequenzbänder" value={dd.wifiBands} />
                  <SpecRow label="Max. WLAN Speed" value={dd.wifiSpeed} accent="#38bdf8" />
                  <SpecRow label="DSL-Standard" value={dd.dslStandard} />
                  <SpecRow label="Kabel-Standard" value={dd.cableStandard} />
                  <SpecRow label="Max. Download" value={dd.maxDownload} accent="#00f5d4" />
                  <SpecRow label="Max. Upload" value={dd.maxUpload} />
                </>
              )}

              {/* Hardware */}
              {(r.processor || r.ram) && (
                <>
                  <SectionHeader icon="⚙️" title="Hardware" color="#a78bfa" />
                  <SpecRow label="Prozessor" value={r.processor} />
                  <SpecRow label="RAM" value={r.ram} />
                  <SpecRow label="Speicher" value={r.storage} />
                </>
              )}

              {/* Ports */}
              {(ports.lan || ports.usb || ports.hdmi) && (
                <>
                  <SectionHeader icon="🔌" title="Anschlüsse" color="#fb923c" />
                  <SpecRow label="LAN-Ports" value={ports.lan ? `${ports.lan}x ${ports.lanSpeed}` : null} />
                  {ports.wan > 0 && <SpecRow label="WAN-Port" value={`${ports.wan}x ${ports.wanSpeed}`} />}
                  <SpecRow label="USB" value={ports.usb !== 'Kein' ? ports.usb : null} />
                  <SpecRow label="Telefon" value={ports.phone !== 'Kein' ? ports.phone : null} />
                  <SpecRow label="DECT" value={ports.dect !== 'Nein' ? ports.dect : null} />
                  {ports.hdmi && <SpecRow label="HDMI" value={ports.hdmi} />}
                  {ports.optical && <SpecRow label="Optisch" value={ports.optical} />}
                  {ports.aux && <SpecRow label="AUX" value={ports.aux} />}
                </>
              )}

              {/* Telephony */}
              {(tel.dect || tel.analog || tel.isdn) && (
                <>
                  <SectionHeader icon="📞" title="Telefonie" color="#00f5d4" />
                  <SpecRow label="DECT" value={tel.dect ? `Ja (bis ${tel.maxPhones} Geräte)` : 'Nein'} />
                  <SpecRow label="Analog" value={tel.analog ? 'Ja' : 'Nein'} />
                  <SpecRow label="ISDN" value={tel.isdn ? 'Ja' : 'Nein'} />
                  <SpecRow label="Anrufbeantworter" value={tel.answering ? 'Ja' : 'Nein'} />
                  <SpecRow label="Fax" value={tel.fax ? 'Ja' : 'Nein'} />
                  {tel.simultaneousCalls > 0 && <SpecRow label="Gleichzeitige Anrufe" value={tel.simultaneousCalls} accent="#00f5d4" />}
                </>
              )}

              {/* Mobilfunk (5G Router) */}
              {mf.simSlot && (
                <>
                  <SectionHeader icon="📱" title="Mobilfunk" color="#fb923c" />
                  <SpecRow label="SIM-Slot" value={mf.simSlot} />
                  <SpecRow label="MIMO" value={mf.mimo} />
                  <SpecRow label="Ext. Antenne" value={mf.externalAntenna} />
                  {mf.bands5G && <SpecRow label="5G Bänder" value={mf.bands5G.join(', ')} />}
                </>
              )}

              {/* Mesh */}
              {mesh.supported && (
                <>
                  <SectionHeader icon="🌐" title="Mesh" color="#a78bfa" />
                  <SpecRow label="Protokoll" value={mesh.protocol} />
                  <SpecRow label="Band Steering" value={mesh.steering ? 'Ja' : 'Nein'} />
                  <SpecRow label="Cross-Band" value={mesh.crossBand ? 'Ja' : 'Nein'} />
                </>
              )}

              {/* Smart Home */}
              {sh.supported && (
                <>
                  <SectionHeader icon="🏠" title="Smart Home" color="#00f5d4" />
                  <SpecRow label="Protokolle" value={sh.protocols?.join(', ')} />
                  {sh.compatibleDevices?.length > 0 && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                      {sh.compatibleDevices.map((d, i) => (
                        <span key={i} style={{ padding: '3px 8px', borderRadius: 6, background: 'rgba(0,245,212,0.06)', border: '1px solid rgba(0,245,212,0.12)', fontSize: '0.52rem', color: 'rgba(200,220,255,0.72)' }}>{d}</span>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Streaming (TV Box) */}
              {stream.os && (
                <>
                  <SectionHeader icon="📺" title="Streaming" color="#ff006e" />
                  <SpecRow label="System" value={stream.os} />
                  <SpecRow label="Auflösung" value={stream.resolution} />
                  <SpecRow label="HDR" value={stream.hdr?.join(', ')} />
                  <SpecRow label="Chromecast" value={stream.chromecast ? 'Ja' : 'Nein'} />
                  <SpecRow label="Google Assist." value={stream.googleAssistant ? 'Ja' : 'Nein'} />
                  {stream.apps?.length > 0 && (
                    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginTop: 4 }}>
                      {stream.apps.map((a, i) => (
                        <span key={i} style={{ padding: '3px 8px', borderRadius: 6, background: 'rgba(255,0,110,0.06)', border: '1px solid rgba(255,0,110,0.12)', fontSize: '0.5rem', color: 'rgba(200,220,255,0.72)' }}>{a}</span>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Audio (TV Box) */}
              {audio.speakers && (
                <>
                  <SectionHeader icon="🔊" title="Audio" color="#a78bfa" />
                  <SpecRow label="Lautsprecher" value={audio.speakers} />
                  <SpecRow label="Gesamtleistung" value={`${audio.totalWatt}W`} accent="#a78bfa" />
                  <SpecRow label="Bluetooth" value={audio.bluetooth ? `Ja (${audio.bluetoothVersion})` : 'Nein'} />
                  <SpecRow label="HDMI ARC" value={audio.hdmiArc ? 'Ja' : 'Nein'} />
                </>
              )}

              {/* Dimensions & Power */}
              {(dim.height || pw.average) && (
                <>
                  <SectionHeader icon="📐" title="Abmessungen & Verbrauch" color="#38bdf8" />
                  {dim.height && <SpecRow label="Maße (H×B×T)" value={`${dim.height} × ${dim.width} × ${dim.depth} mm`} />}
                  {dim.weight && <SpecRow label="Gewicht" value={`${dim.weight} g`} />}
                  <SpecRow label="Verbrauch (Ø)" value={pw.average} />
                  <SpecRow label="Verbrauch (max)" value={pw.max} />
                </>
              )}

              {/* Security */}
              {r.security && r.security.length > 0 && (
                <>
                  <SectionHeader icon="🔒" title="Sicherheit" color="#00f5d4" />
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {r.security.map((s, i) => (
                      <span key={i} style={{ padding: '4px 10px', borderRadius: 8, background: 'rgba(0,245,212,0.06)', border: '1px solid rgba(0,245,212,0.12)', fontSize: '0.55rem', color: 'rgba(200,220,255,0.8)' }}>🔐 {s}</span>
                    ))}
                  </div>
                </>
              )}

              {/* Features */}
              {r.features && r.features.length > 0 && (
                <div style={{ marginTop: 12, marginBottom: 8 }}>
                  <div style={{ fontSize: '0.52rem', fontWeight: 700, color: 'rgba(200,220,255,0.65)', marginBottom: 6, textTransform: 'uppercase' }}>Features</div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {r.features.map((f, i) => (
                      <span key={i} style={{ padding: '4px 10px', borderRadius: 8, background: 'rgba(251,146,60,0.06)', border: '1px solid rgba(251,146,60,0.15)', fontSize: '0.58rem', color: 'rgba(200,220,255,0.82)' }}>✓ {f}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Special Features */}
              {r.specialFeatures && r.specialFeatures.length > 0 && (
                <>
                  <SectionHeader icon="✨" title="Besondere Features" color="#ff006e" />
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {r.specialFeatures.map((f, i) => (
                      <span key={i} style={{ padding: '4px 10px', borderRadius: 8, background: 'rgba(255,0,110,0.06)', border: '1px solid rgba(255,0,110,0.15)', fontSize: '0.55rem', color: 'rgba(200,220,255,0.82)' }}>⭐ {f}</span>
                    ))}
                  </div>
                </>
              )}

              {/* Meta */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                {r.warranty && <span style={{ fontSize: '0.5rem', color: 'rgba(200,220,255,0.5)' }}>🛡️ {r.warranty} Garantie</span>}
                {r.energyClass && r.energyClass !== '-' && <span style={{ fontSize: '0.5rem', color: 'rgba(200,220,255,0.5)' }}>⚡ Energie: {r.energyClass}</span>}
                {r.ean && <span style={{ fontSize: '0.5rem', color: 'rgba(200,220,255,0.5)' }}>📦 EAN: {r.ean}</span>}
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <button onClick={() => { window.location.hash = '#/konfigurator'; setSelectedRouter(null); }}
                  style={{ flex: 1, padding: '12px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg, #fb923c, #f97316)', color: '#fff', fontSize: '0.72rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 20px rgba(251,146,60,0.3)' }}>
                  📋 Im Konfigurator verwenden
                </button>
                <button onClick={() => setDetailTab('infoblatt')}
                  style={{ padding: '12px 16px', borderRadius: 12, border: '1px solid rgba(0,245,212,0.3)', background: 'rgba(0,245,212,0.06)', color: '#00f5d4', fontSize: '0.72rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}>
                  🖨️
                </button>
              </div>
            </>
          )}

          {/* ===== INFOBLATT TAB ===== */}
          {detailTab === 'infoblatt' && (
            <>
              <div style={{ borderRadius: 16, border: '1px solid rgba(251,146,60,0.15)', padding: '20px', background: 'rgba(10,20,50,0.5)' }}>
                {/* Title Block */}
                <div style={{ textAlign: 'center', marginBottom: 18, paddingBottom: 14, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ fontSize: '1.3rem', fontWeight: 900, color: '#fff' }}>{r.name}</div>
                  <div style={{ fontSize: '0.72rem', color: 'rgba(200,220,255,0.7)', marginTop: 4 }}>{r.brand} · {TYPE_LABELS[r.type] || r.type}</div>
                  {r.ean && <div style={{ fontSize: '0.55rem', color: 'rgba(200,220,255,0.45)', marginTop: 4 }}>EAN: {r.ean}</div>}
                </div>

                {/* Two Column Spec Layout */}
                <div className="spec-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  {/* Left Column */}
                  <div>
                    <div style={{ fontSize: '0.65rem', fontWeight: 800, color: '#fb923c', marginBottom: 6 }}>📶 WLAN & Internet</div>
                    <SpecRow label="WLAN-Std." value={dd.wifiStandard || r.wifi} />
                    <SpecRow label="Bänder" value={dd.wifiBands} />
                    <SpecRow label="WLAN Speed" value={dd.wifiSpeed} />
                    <SpecRow label="Max. Download" value={dd.maxDownload || r.maxSpeed} />
                    <SpecRow label="Max. Upload" value={dd.maxUpload} />
                    <SpecRow label="DSL" value={dd.dslStandard} />
                    <SpecRow label="Kabel" value={dd.cableStandard} />

                    <div style={{ fontSize: '0.65rem', fontWeight: 800, color: '#38bdf8', marginBottom: 6, marginTop: 12 }}>⚙️ Hardware</div>
                    <SpecRow label="Prozessor" value={r.processor} />
                    <SpecRow label="RAM" value={r.ram} />
                    <SpecRow label="Speicher" value={r.storage} />

                    <div style={{ fontSize: '0.65rem', fontWeight: 800, color: '#a78bfa', marginBottom: 6, marginTop: 12 }}>🔌 Anschlüsse</div>
                    <SpecRow label="LAN" value={ports.lan ? `${ports.lan}x ${ports.lanSpeed}` : null} />
                    {ports.wan > 0 && <SpecRow label="WAN" value={`${ports.wan}x ${ports.wanSpeed}`} />}
                    <SpecRow label="USB" value={ports.usb !== 'Kein' ? ports.usb : null} />
                    <SpecRow label="Telefon" value={ports.phone !== 'Kein' ? ports.phone : null} />
                    {ports.hdmi && <SpecRow label="HDMI" value={ports.hdmi} />}
                    {ports.optical && <SpecRow label="Optisch" value={ports.optical} />}
                  </div>

                  {/* Right Column */}
                  <div>
                    {(tel.dect || tel.analog) && (
                      <>
                        <div style={{ fontSize: '0.65rem', fontWeight: 800, color: '#00f5d4', marginBottom: 6 }}>📞 Telefonie</div>
                        <SpecRow label="DECT" value={tel.dect ? `Ja (${tel.maxPhones} Geräte)` : 'Nein'} />
                        <SpecRow label="Analog" value={tel.analog ? 'Ja' : 'Nein'} />
                        <SpecRow label="ISDN" value={tel.isdn ? 'Ja' : 'Nein'} />
                        <SpecRow label="AB/Fax" value={`${tel.answering ? 'AB' : '-'} / ${tel.fax ? 'Fax' : '-'}`} />
                      </>
                    )}

                    {mf.simSlot && (
                      <>
                        <div style={{ fontSize: '0.65rem', fontWeight: 800, color: '#fb923c', marginBottom: 6, marginTop: tel.dect ? 12 : 0 }}>📱 Mobilfunk</div>
                        <SpecRow label="SIM" value={mf.simSlot} />
                        <SpecRow label="MIMO" value={mf.mimo} />
                        <SpecRow label="Ext. Antenne" value={mf.externalAntenna} />
                      </>
                    )}

                    {mesh.supported && (
                      <>
                        <div style={{ fontSize: '0.65rem', fontWeight: 800, color: '#a78bfa', marginBottom: 6, marginTop: 12 }}>🌐 Mesh</div>
                        <SpecRow label="Protokoll" value={mesh.protocol} />
                        <SpecRow label="Steering" value={mesh.steering ? 'Ja' : 'Nein'} />
                        <SpecRow label="Cross-Band" value={mesh.crossBand ? 'Ja' : 'Nein'} />
                      </>
                    )}

                    <div style={{ fontSize: '0.65rem', fontWeight: 800, color: '#38bdf8', marginBottom: 6, marginTop: 12 }}>📐 Gehäuse</div>
                    {dim.height && <SpecRow label="Maße" value={`${dim.height}×${dim.width}×${dim.depth} mm`} />}
                    {dim.weight && <SpecRow label="Gewicht" value={`${dim.weight} g`} />}
                    <SpecRow label="Verbrauch" value={pw.average ? `Ø ${pw.average} / max ${pw.max}` : null} />
                    {r.energyClass && r.energyClass !== '-' && <SpecRow label="Effizienz" value={`Klasse ${r.energyClass}`} />}

                    {r.specialFeatures && r.specialFeatures.length > 0 && (
                      <>
                        <div style={{ fontSize: '0.65rem', fontWeight: 800, color: '#ff006e', marginBottom: 6, marginTop: 12 }}>✨ Features</div>
                        {r.specialFeatures.slice(0, 6).map((f, i) => (
                          <div key={i} style={{ fontSize: '0.55rem', color: 'rgba(200,220,255,0.75)', padding: '2px 0' }}>• {f}</div>
                        ))}
                      </>
                    )}
                  </div>
                </div>

                {/* Price + Security Footer */}
                <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: '0.5rem', color: 'rgba(200,220,255,0.5)' }}>Preise</div>
                    <div style={{ fontSize: '0.72rem', fontWeight: 800, color: '#00f5d4' }}>VK: {fmtEuro(r.price)}{r.monthlyRental > 0 ? ` / ${fmtEuro(r.monthlyRental)}/mtl.` : ''}</div>
                    {r.purchasePrice > 0 && <div style={{ fontSize: '0.55rem', color: '#fb923c' }}>EK: {fmtEuro(r.purchasePrice)}</div>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {r.security && <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.45)' }}>🔐 {r.security.join(', ')}</div>}
                    {r.warranty && <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.45)' }}>🛡️ {r.warranty}</div>}
                  </div>
                </div>

                {/* Package Contents */}
                {r.packageContents && (
                  <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                    <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.45)' }}>📦 Lieferumfang: {r.packageContents.join(' · ')}</div>
                  </div>
                )}
              </div>

              {/* Print Button */}
              <div className="no-print" style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <button onClick={() => setDetailTab('details')}
                  style={{ padding: '12px 16px', borderRadius: 12, border: '1px solid rgba(251,146,60,0.3)', background: 'rgba(251,146,60,0.06)', color: '#fb923c', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                  ← Zurück
                </button>
                <button onClick={handlePrint}
                  style={{ flex: 1, padding: '12px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg, #00f5d4, #0d9488)', color: '#fff', fontSize: '0.72rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 20px rgba(0,245,212,0.25)' }}>
                  🖨️ Infoblatt drucken
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', color: '#fff', fontFamily: "'Poppins', system-ui, sans-serif" }}>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '12px 16px 100px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 900, background: 'linear-gradient(135deg, #fb923c, #f97316, #38bdf8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>📡 Router & Hardware</div>
          <div style={{ fontSize: '0.68rem', color: 'rgba(200,220,255,0.72)', marginTop: 4 }}>{routers.length} Geräte verfügbar</div>
        </div>

        {/* Filter */}
        <GlassCard glow="orange" style={{ marginBottom: 16 }}>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Router suchen..."
            style={{ width: '100%', padding: '12px 14px', borderRadius: 14, border: '1px solid rgba(251,146,60,0.2)', background: 'rgba(251,146,60,0.04)', color: '#fff', fontSize: '0.82rem', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', marginBottom: 10 }} />
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {types.map(t => (
              <button key={t} onClick={() => setSelectedType(t)}
                style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${selectedType === t ? 'rgba(251,146,60,0.4)' : 'rgba(255,255,255,0.06)'}`, background: selectedType === t ? 'rgba(251,146,60,0.12)' : 'rgba(255,255,255,0.02)', color: selectedType === t ? '#fb923c' : 'rgba(200,220,255,0.72)', fontSize: '0.62rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                {t === 'all' ? 'Alle' : (TYPE_LABELS[t] || t)}
              </button>
            ))}
          </div>
        </GlassCard>

        {/* Router Detail Modal */}
        {renderDetailModal()}

        {/* View Toggle */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(200,220,255,0.6)' }}>{filtered.length} Ergebnis{filtered.length !== 1 ? 'se' : ''}</div>
          <div style={{ display: 'flex', background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 3, border: '1px solid rgba(255,255,255,0.06)' }}>
            <button onClick={() => setViewMode('grid')}
              style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: viewMode === 'grid' ? 'rgba(251,146,60,0.15)' : 'transparent', color: viewMode === 'grid' ? '#fb923c' : 'rgba(200,220,255,0.5)', fontSize: '0.65rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.2s' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
              Kacheln
            </button>
            <button onClick={() => setViewMode('list')}
              style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: viewMode === 'list' ? 'rgba(251,146,60,0.15)' : 'transparent', color: viewMode === 'list' ? '#fb923c' : 'rgba(200,220,255,0.5)', fontSize: '0.65rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.2s' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
              Liste
            </button>
          </div>
        </div>

        {/* Router Grid / List */}
        {isLoading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {[1,2,3,4,5,6].map(i => (
               <GlassCard key={i} glow="orange" style={{ opacity: 0.4 }}>
                 <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                   <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,0.05)' }} />
                   <div style={{ flex: 1 }}>
                     <div style={{ height: 14, background: 'rgba(255,255,255,0.1)', borderRadius: 4, marginBottom: 6 }} />
                     <div style={{ height: 10, width: '60%', background: 'rgba(255,255,255,0.05)', borderRadius: 4 }} />
                   </div>
                 </div>
                 <div style={{ height: 32, background: 'rgba(255,255,255,0.05)', borderRadius: 6, marginTop: 16 }} />
                 <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
                   <div style={{ height: 16, width: '30%', background: 'rgba(255,255,255,0.05)', borderRadius: 4 }} />
                   <div style={{ height: 16, width: '20%', background: 'rgba(255,255,255,0.05)', borderRadius: 4 }} />
                 </div>
               </GlassCard>
            ))}
          </div>
        ) : viewMode === 'grid' ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {filtered.map(router => (
              <GlassCard key={router.id} glow={TYPE_COLORS[router.type] || 'cyan'} style={{ cursor: 'pointer', transition: 'transform 0.2s' }} onClick={() => openRouter(router)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(251,146,60,0.06)', border: '1px solid rgba(251,146,60,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', flexShrink: 0 }}>{TYPE_ICONS[router.type] || '📡'}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{router.name}</div>
                    <div style={{ fontSize: '0.55rem', color: 'rgba(200,220,255,0.65)' }}>{TYPE_LABELS[router.type] || router.type}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                  <div>
                    {router.wifi && <div style={{ fontSize: '0.55rem', color: 'rgba(200,220,255,0.65)' }}>{router.wifi}</div>}
                    {router.maxSpeed && router.maxSpeed !== 'Repeater' && router.maxSpeed !== 'Streaming-Box' && <div style={{ fontSize: '0.55rem', color: 'rgba(200,220,255,0.55)' }}>bis {router.maxSpeed}</div>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.95rem', fontWeight: 900, color: '#00f5d4' }}>{fmtEuro(router.price)}</div>
                    {router.monthlyRental > 0 && <div style={{ fontSize: '0.5rem', color: 'rgba(200,220,255,0.5)' }}>{fmtEuro(router.monthlyRental)}/mtl.</div>}
                  </div>
                </div>
                {/* Quick spec badges */}
                <div style={{ display: 'flex', gap: 3, marginTop: 8, flexWrap: 'wrap' }}>
                  {router.processor && <span style={{ padding: '2px 7px', borderRadius: 6, background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.12)', fontSize: '0.44rem', color: 'rgba(200,220,255,0.65)' }}>⚡ {router.processor.split(' ').slice(0,2).join(' ')}</span>}
                  {router.mesh?.supported && <span style={{ padding: '2px 7px', borderRadius: 6, background: 'rgba(0,245,212,0.06)', border: '1px solid rgba(0,245,212,0.12)', fontSize: '0.44rem', color: 'rgba(200,220,255,0.65)' }}>🌐 Mesh</span>}
                  {router.ports?.lan > 0 && <span style={{ padding: '2px 7px', borderRadius: 6, background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.12)', fontSize: '0.44rem', color: 'rgba(200,220,255,0.65)' }}>🔌 {router.ports.lan}x LAN</span>}
                  {router.telephony?.dect && <span style={{ padding: '2px 7px', borderRadius: 6, background: 'rgba(251,146,60,0.06)', border: '1px solid rgba(251,146,60,0.12)', fontSize: '0.44rem', color: 'rgba(200,220,255,0.65)' }}>📞 DECT</span>}
                </div>
              </GlassCard>
            ))}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {filtered.map(router => (
              <GlassCard key={router.id} glow={TYPE_COLORS[router.type] || 'cyan'} style={{ cursor: 'pointer', padding: '12px 16px', transition: 'transform 0.2s' }} onClick={() => openRouter(router)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(251,146,60,0.06)', border: '1px solid rgba(251,146,60,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', flexShrink: 0 }}>{TYPE_ICONS[router.type] || '📡'}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{router.name}</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 2 }}>
                      <span style={{ padding: '2px 8px', borderRadius: 5, background: 'rgba(251,146,60,0.08)', fontSize: '0.48rem', fontWeight: 700, color: '#fb923c' }}>{TYPE_LABELS[router.type] || router.type}</span>
                      {router.wifi && <span style={{ fontSize: '0.52rem', color: 'rgba(200,220,255,0.55)' }}>{router.wifi}</span>}
                      {router.mesh?.supported && <span style={{ fontSize: '0.48rem', color: '#a78bfa' }}>🌐 Mesh</span>}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: '0.88rem', fontWeight: 900, color: '#00f5d4' }}>{fmtEuro(router.price)}</div>
                    {router.monthlyRental > 0 && <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.5)' }}>{fmtEuro(router.monthlyRental)}/mtl.</div>}
                  </div>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(200,220,255,0.35)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><polyline points="9 18 15 12 9 6"/></svg>
                </div>
              </GlassCard>
            ))}
          </div>
        )}
        {!isLoading && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'rgba(200,220,255,0.5)', fontSize: '0.82rem' }}>Keine Router gefunden</div>
        )}
      </div>
    </div>
  );
}

export default RouterDB;
